import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '32px 20px 40px',
    maxWidth: 1200,
    margin: '0 auto',
    textAlign: 'left',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    rowGap: 8,
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: 800,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.8)',
  },
  version: {
    fontSize: '1rem',
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid #4d79ff',
    background: 'rgba(19, 24, 48, 0.85)',
  },
  subtitle: {
    marginTop: 12,
    color: '#cfd8ff',
    maxWidth: 720,
    lineHeight: 1.5,
    textAlign: 'left',
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
    gap: 24,
    marginTop: 32,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  card: {
    borderRadius: 16,
    padding: 16,
    background: 'linear-gradient(135deg, rgba(16,24,40,0.9), rgba(10,14,30,0.95))',
    border: '1px solid rgba(77, 121, 255, 0.4)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.45)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
  },
  pill: {
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid rgba(207,216,255,0.6)',
    color: '#cfd8ff',
  },
  list: {
    margin: '8px 0 0',
    paddingLeft: 18,
    fontSize: 14,
    color: '#e5e7ff',
    textAlign: 'left',
  },
  listItemHighlight: {
    color: '#9ef7c5',
    fontWeight: 600,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  callout: {
    borderRadius: 16,
    padding: 16,
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.35), rgba(10,14,30,0.98))',
    border: '1px solid rgba(77, 121, 255, 0.6)',
  },
  calloutTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    marginBottom: 6,
  },
  calloutText: {
    fontSize: 14,
    color: '#dde3ff',
    lineHeight: 1.6,
    textAlign: 'left',
  },
  footer: {
    marginTop: 32,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: 12,
    fontSize: 13,
    color: '#8e95c9',
  },
  linkButton: {
    borderRadius: 999,
    border: '1px solid #4d79ff',
    background: 'transparent',
    color: '#e5e7ff',
    padding: '6px 12px',
    fontSize: 13,
    cursor: 'pointer',
  },
}));

const WhatsNewPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      {/* Latest release: 1.1.4 */}
      <div className={classes.headerRow}>
        <div>
          <div className={classes.title}>What&apos;s New in E3D</div>
          <div className={classes.subtitle}>
            This release introduces an intuitive dashboard that feeds you directly into the 3D
            token graph, plus a new Live mode that keeps your view up to date without extra clicks.
          </div>
        </div>
        <div className={classes.version}>Version 1.1.4</div>
      </div>

      <div className={classes.sectionGrid}>
        <div className={classes.leftColumn}>
          <section className={classes.card}>
            <div className={classes.cardHeader}>
              <button
                type="button"
                className={classes.cardTitle}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onClick={() => navigate('/dashboard')}
              >
                Dashboard‑Driven Token Workflows
              </button>
              <span
                className={classes.pill}
                style={{
                  background: 'none',
                  border: '1px solid rgba(207,216,255,0.6)',
                }}
              >
                Dashboard
              </span>
            </div>
            <ul className={classes.list}>
              <li>
                Start from the new dashboard widgets —{' '}
                <span className={classes.listItemHighlight}>Top Gainers</span>,{' '}
                <span className={classes.listItemHighlight}>Top Losers</span>,{' '}
                <span className={classes.listItemHighlight}>Top Tokens</span>, and{' '}
                <span className={classes.listItemHighlight}>Latest Tokens</span>, plus the new{' '}
                <span className={classes.listItemHighlight}>Top Volume</span> widget with mini
                volume sparklines — and click a token to jump straight into its 3D graph view at{' '}
                <span className={classes.listItemHighlight}>/links/&lt;address&gt;</span>.
              </li>
              <li>
                The token list and the sphere stay in sync: any token that appears in the graph is
                reflected in the list with updated activity sparklines as you explore.
              </li>
              <li>
                From the graph, use <span className={classes.listItemHighlight}>Follow Transactions</span> to pull in
                counterparties and follow real flows across the blockchain, all from the same
                dashboard‑driven workflow.
              </li>
            </ul>
          </section>

          <section className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardTitle}>Follow Workflow in the Graph</div>
              <span className={classes.pill}>Graph Workflow</span>
            </div>
            <ul className={classes.list}>
              <li>
                Right‑click any token node in the <span className={classes.listItemHighlight}>/token</span> view and
                choose <span className={classes.listItemHighlight}>Follow</span> to pull in counterparties that have
                been trading with that token.
              </li>
              <li>
                New nodes are added to the existing sphere instead of resetting it, so you can grow
                an exploratory graph step by step.
              </li>
              <li>
                Node sizes now scale with link count (degree), capped so the most connected tokens
                stand out without overwhelming the view.
              </li>
              <li>
                The overall flow stays simple: spot a token on the dashboard, open its graph, then
                follow paths through counterparties without losing your place.
              </li>
            </ul>
          </section>

          <section className={classes.card}>
            <div className={classes.cardHeader}>
              <div className={classes.cardTitle}>Live Graph Mode</div>
              <span className={classes.pill}>Live</span>
            </div>
            <ul className={classes.list}>
              <li>
                Turn on <span className={classes.listItemHighlight}>Live</span> in the graph toolbar to have E3D do
                the work for you: it smoothly pulls in new transactions every minute and adds them to
                the existing graph instead of rebuilding from scratch.
              </li>
              <li>
                The view automatically maintains a rolling{' '}
                <span className={classes.listItemHighlight}>20‑minute window</span> of activity, pruning nodes and
                links that haven&apos;t seen a transaction in the last twenty minutes so the graph stays
                focused.
              </li>
              <li>
                New links briefly appear brighter and thicker, and new nodes are seeded near their
                existing neighbors so the layout stays readable even as the graph evolves.
              </li>
            </ul>
          </section>
        </div>

        <aside className={classes.rightColumn}>
          <div className={classes.callout}>
            <div className={classes.calloutTitle}>Why this matters</div>
            <p className={classes.calloutText}>
              The dashboard is now the front door to the graph: you can spot interesting tokens,
              open them in the 3D view, turn on Live, and watch real flows update themselves while
              you follow paths through the network.
            </p>
          </div>
          <div className={classes.callout}>
            <div className={classes.calloutTitle}>How to try it</div>
            <p className={classes.calloutText}>
              Land on{' '}
              <button
                type="button"
                className={classes.listItemHighlight}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                }}
                onClick={() => navigate('/')}
              >
                Dashboard
              </button>
              , click a token in the widgets to open its graph, turn on
              <span className={classes.listItemHighlight}> Live</span> in the toolbar to let new
              transactions stream in every minute, then right‑click nodes and choose
              <span className={classes.listItemHighlight}> Follow Transactions</span> to chase flows
              through the rolling 20‑minute window.
            </p>
          </div>
        </aside>
      </div>

      {/* Previous release: 1.1.2 and earlier */}
      <div style={{ marginTop: 40 }}>
        <div className={classes.headerRow}>
          <div>
            <div className={classes.title} style={{ fontSize: '1.6rem' }}>Previously in E3D</div>
            <div className={classes.subtitle}>
              Earlier updates introduced the stories‑driven explorer, token price and volatility
              modes, chain spheres, and cleaner token lists with categories.
            </div>
          </div>
          <div className={classes.version}>Version 1.1.3</div>
        </div>

        <div className={classes.sectionGrid}>
          <div className={classes.leftColumn}>
            <section className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardTitle}>Price &amp; Volatility Tokens View</div>
                <span className={classes.pill}>Token List</span>
              </div>
              <ul className={classes.list}>
                <li>
                  Tokens can be sorted by <span className={classes.listItemHighlight}>Price</span> or
                  <span className={classes.listItemHighlight}> Volatility</span> over different time
                  windows, so it&apos;s easy to spot movers.
                </li>
                <li>
                  Market cap and other columns make it easier to compare big caps, mid caps and
                  tiny experiments side by side.
                </li>
              </ul>
            </section>

            <section className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardTitle}>Chain Spheres</div>
                <span className={classes.pill}>3D View</span>
              </div>
              <ul className={classes.list}>
                <li>
                  Tokens can be arranged into <span className={classes.listItemHighlight}>3D chain
                  spheres</span>, where each chain becomes its own globe of tokens.
                </li>
                <li>
                  A gentle camera orbit makes it feel like you&apos;re flying around a system of
                  planets instead of a flat list.
                </li>
              </ul>
            </section>

            <section className={classes.card}>
              <div className={classes.cardHeader}>
                <div className={classes.cardTitle}>Cleaner Categories &amp; Lists</div>
                <span className={classes.pill}>Discovery</span>
              </div>
              <ul className={classes.list}>
                <li>
                  Tokens are grouped more cleanly by category, making it easier to jump between
                  stablecoins, DeFi, gaming, and more.
                </li>
                <li>
                  The sidebar views were simplified so columns always match the active mode.
                </li>
              </ul>
            </section>
          </div>

          <aside className={classes.rightColumn}>
            <div className={classes.callout}>
              <div className={classes.calloutTitle}>Explore both releases</div>
              <p className={classes.calloutText}>
                Use the Tokens shortcuts and layout buttons to switch between classic list views,
                price/volatility rankings, and the 3D chain spheres.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <footer className={classes.footer}>
        <span>Tip: click any token in the graph to open the new token popup, or use Tokens → Price / Volatility to explore earlier features.</span>
        <button
          className={classes.linkButton}
          onClick={() => navigate('/home')}
        >
          Back to Home
        </button>
      </footer>
    </div>
  );
};

export default WhatsNewPage;
